import { cloneDeep, get, isEmpty, set, sortBy } from 'lodash'
import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../core/i18n'
import { postPriceService } from '../../core/services'
import * as constants from '../../static/constants'
import * as interfaces from '../../static/interfaces'
import { Button, Spinner } from '../atoms'

declare var window: interfaces.IWindow

interface IPostPrice {
  id?: number
  price: string
  pricing_type: string
  max_people: string
}

interface IProps {
  post: interfaces.ICustomPost
}

const PostPrivateTourPrice: React.FC<IProps> = props => {
  const initialPostPrice = React.useMemo(() => {
    return props.post.post_prices?.length
      ? sortBy(
          props.post.post_prices
            .filter(({ pricing_type }) => pricing_type === 'private')
            .map(({ id, pricing_type, price, max_people }) => ({
              id,
              pricing_type,
              price,
              max_people,
            })),
          'max_people'
        )
      : [
          {
            price: '0',
            max_people: '',
            pricing_type: 'private',
          },
        ]
  }, [])

  const [loading, setLoading] = React.useState(false)
  const [errors, setErrors] = React.useState({})
  const [postPrices, setPostPrices] = React.useState<IPostPrice[]>(initialPostPrice)

  const onDeletePriceSetting = () => {
    const nextPostPrices = cloneDeep(postPrices)
    nextPostPrices.splice(-1, 1)
    setPostPrices(nextPostPrices)
  }

  const onAddPriceSetting = () => {
    const currentErrors = validateFields()

    if (!isEmpty(currentErrors)) {
      return
    }

    const nextPostPrices = cloneDeep(postPrices)
    nextPostPrices.push({
      price: postPrices[postPrices.length - 1]?.price || '',
      max_people: '',
      pricing_type: 'private',
    })
    setPostPrices(nextPostPrices)
  }

  const onInputChange = event => {
    const nextPostPrices = cloneDeep(postPrices)

    set(nextPostPrices, event.target.name, event.target.value)
    setPostPrices(nextPostPrices)
  }

  const validateFields = () => {
    const nextErrors = {}

    postPrices.forEach((postPrice, index) => {
      if (postPrice.price === '' || isNaN(Number(postPrice.price))) {
        nextErrors[`[${index}].price`] = 'please input number'
      }

      if (postPrice.max_people === '' || isNaN(Number(postPrice.max_people))) {
        nextErrors[`[${index}].max_people`] = 'please input number'
      } else if (
        index > 0 &&
        Number(postPrice.max_people) < Number(postPrices[index - 1]?.max_people)
      ) {
        nextErrors[`[${index}].max_people`] = `please input over ${Number(
          postPrices[index - 1]?.max_people
        ) + 1}`
      }
    })

    setErrors(nextErrors)
    return nextErrors
  }

  const submit = async () => {
    if (loading) {
      return
    }

    const currentErrors = validateFields()
    let params = cloneDeep(postPrices)

    if (!isEmpty(currentErrors)) {
      return
    }

    setLoading(true)

    if (initialPostPrice.length > postPrices.length) {
      const cloned = cloneDeep(initialPostPrice)
      cloned.splice(0, postPrices.length)
      params = params.concat(cloned.map(postPrice => ({ id: postPrice.id, _destroy: true })))
    }

    try {
      const { postPrices: updatedPostPrices, flush } = await postPriceService.update(
        props.post.id,
        params
      )
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      const updated = updatedPostPrices
        .filter(({ pricing_type }) => pricing_type === 'private')
        .map(({ id, pricing_type, price, max_people }) => ({
          id,
          pricing_type,
          price,
          max_people,
        }))
      const sorted = sortBy(updated, 'max_people')
      setPostPrices(sorted)
    } catch (e) {
      const message = e.response?.data?.errors || I18n.t('flash.unexpected_error')
      window.flashMessages.addMessage({ text: message, type: 'error' })
    } finally {
      setLoading(false)
    }
  }
  const handleSubmit = async event => {
    event.preventDefault()
    await submit()
  }

  return (
    <Wrapper>
      <h2 className="Editor_Title">{I18n.t('post.set_price')}</h2>
      <div className="main">
        <form onSubmit={handleSubmit}>
          <ul>
            <li>Price</li>
            <li>Number of people</li>
            <li className="buttons" />
          </ul>
          {postPrices.map((postPrice, index) => {
            return (
              <ul key={index} className="PriceItem">
                <li>
                  <div>
                    <input
                      type="text"
                      name={`[${index}].price`}
                      value={postPrices[index]?.price}
                      onChange={onInputChange}
                    />
                    {get(errors, `[${index}].price`) && (
                      <span className="error">{get(errors, `[${index}].price`)}</span>
                    )}
                  </div>
                </li>
                <li className="People">
                  <div className="min">{Number(postPrices[index - 1]?.max_people) + 1 || 0}</div>
                  <div className="separator">~</div>
                  <div>
                    <input
                      type="text"
                      name={`[${index}].max_people`}
                      value={postPrices[index]?.max_people}
                      onChange={onInputChange}
                    />
                    {get(errors, `[${index}].max_people`) && (
                      <span className="error">{get(errors, `[${index}].max_people`)}</span>
                    )}
                  </div>
                </li>
                <li className="buttons">
                  {index > 0 && index === postPrices.length - 1 && (
                    <Button small={true} handleClick={onDeletePriceSetting}>
                      Delete
                    </Button>
                  )}
                </li>
              </ul>
            )
          })}
          <div className="Add">
            <Button handleClick={onAddPriceSetting}>+ Add price setting</Button>
          </div>
          <div className="Footer">
            {loading ? <Spinner /> : <Button primary={true}>Update</Button>}
          </div>
        </form>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  ul {
    display: flex;
  }

  li {
    width: 50%;
    padding: 8px 0;

    &.buttons {
      width: 240px;
    }
  }

  li + li {
    margin-left: 24px;
  }

  .Add {
    display: flex;
    justify-content: center;
    margin: 12px 0 24px;
  }

  .Footer {
    border-top: solid 1px ${constants.COLORS.divider};
    padding-top: 12px;
    display: flex;
    justify-content: center;
  }

  .PriceItem {
  }

  .People {
    display: flex;
    align-items: center;

    .min,
    .separator {
      width: 32px;
    }
  }

  .error {
    color: ${constants.COLORS.cautionText};
  }

  .main {
    padding: 24px;
  }

  input {
    display: block;
    width: 100%;
    padding: 8px 12px;
    border: solid 1px ${constants.COLORS.divider};
    border-radius: 4px;
    font-size: 15px;
    transition: border 0.2s ease;
    outline: none;
    background: none;

    &:not([readonly]):focus {
      border: solid 1px ${constants.COLORS.primary};
    }
  }
`

export default PostPrivateTourPrice
